import React from "react";
import { useNavigate } from "react-router-dom";
import { PipelineStage, PipelineBorrower } from "@/types/dashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getUserDisplayName } from "@/utils/emailFormatter";

interface DealPipelineProps {
  pipeline: PipelineStage[];
  isLoading: boolean;
}

const DealCard: React.FC<{ borrower: PipelineBorrower; color: string }> = ({ borrower, color }) => {
  const navigate = useNavigate();

  const getRelativeTime = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  return (
    <button
      type="button"
      className={cn(
        "mb-3 w-full rounded-lg border bg-card p-4 text-left transition-all hover:-translate-y-px hover:shadow-md"
      )}
      style={
        {
          borderColor: "hsl(var(--border))",
        } as React.CSSProperties
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.boxShadow = `0 2px 8px ${color}33`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "";
        e.currentTarget.style.boxShadow = "";
      }}
      onClick={() => navigate(`/applications/${borrower.id}`)}
    >
      <p className="truncate text-sm font-semibold">{borrower.company_name}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="max-w-[60%] truncate text-xs text-muted-foreground">
          {getUserDisplayName(borrower.broker_name, borrower.broker_email, borrower.owner_name)}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {getRelativeTime(borrower.created_at)}
        </span>
      </div>
    </button>
  );
};

const PipelineColumn: React.FC<{ stage: PipelineStage; isLoading: boolean }> = ({
  stage,
  isLoading,
}) => {
  const navigate = useNavigate();
  const displayBorrowers = stage.borrowers.slice(0, 8);

  return (
    <Card
      className="flex h-full flex-col overflow-hidden border-t-4 shadow-sm"
      style={{ borderTopColor: stage.color }}
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold">{stage.label}</span>
          <Badge
            variant="secondary"
            className="min-w-8 justify-center font-bold"
            style={{ backgroundColor: `${stage.color}26`, color: stage.color }}
          >
            {isLoading ? "..." : stage.count}
          </Badge>
        </div>
      </div>

      <CardContent className="max-h-[480px] min-h-[200px] flex-1 overflow-y-auto bg-[#fafbfc] px-4 py-3">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[72px] w-full rounded-lg" />
            ))}
          </div>
        ) : displayBorrowers.length > 0 ? (
          displayBorrowers.map((borrower) => (
            <DealCard key={borrower.id} borrower={borrower} color={stage.color} />
          ))
        ) : (
          <div className="flex h-[120px] items-center justify-center">
            <p className="text-sm italic text-muted-foreground">No deals in this stage</p>
          </div>
        )}
      </CardContent>

      {stage.count > 8 && (
        <button
          type="button"
          className="border-t px-4 py-3 text-center text-xs font-semibold transition-colors hover:bg-muted/50"
          style={{ color: stage.color }}
          onClick={() => navigate(`/applications?app_status=${stage.status}`)}
        >
          View all {stage.count} deals
        </button>
      )}
    </Card>
  );
};

const DealPipeline: React.FC<DealPipelineProps> = ({ pipeline, isLoading }) => {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {pipeline.map((stage) => (
        <PipelineColumn key={stage.status} stage={stage} isLoading={isLoading} />
      ))}
    </div>
  );
};

export default DealPipeline;
